import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDuration } from '../common';
import { SyncController } from './SyncController';

export interface ComparisonPlayerProps {
  referenceSrc: string | null;
  attemptSrc: string | null;
  referenceLabel?: string;
  attemptLabel?: string;
  initialOffset?: number;
}

export function ComparisonPlayer({
  referenceSrc,
  attemptSrc,
  referenceLabel,
  attemptLabel,
  initialOffset = 0,
}: ComparisonPlayerProps) {
  const { t } = useTranslation();
  const refAudio = useRef<HTMLAudioElement>(null);
  const attemptAudio = useRef<HTMLAudioElement>(null);
  const controller = useRef<SyncController | null>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [length, setLength] = useState(0);
  const [offset, setOffset] = useState(initialOffset);
  const [muteRef, setMuteRef] = useState(false);
  const [muteAttempt, setMuteAttempt] = useState(false);
  const seekId = useId();
  const offsetId = useId();

  useEffect(() => {
    if (!refAudio.current || !attemptAudio.current) return;
    controller.current = new SyncController(refAudio.current, attemptAudio.current, initialOffset);
    setPlaying(false);
    setPosition(0);
    return () => controller.current?.pause();
  }, [referenceSrc, attemptSrc, initialOffset]);

  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    const loop = () => {
      const c = controller.current;
      if (!c) return;
      setPosition(c.tick());
      setLength(c.length);
      if (!c.playing) {
        setPlaying(false);
        return;
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  const ready = Boolean(referenceSrc && attemptSrc);
  const refreshLength = () => setLength(controller.current?.length ?? 0);

  return (
    <section className="card comparison-player" aria-label={t('compare.title')}>
      <div className="tracks">
        <div className="track">
          <h3>{referenceLabel ?? t('compare.reference')}</h3>
          <audio ref={refAudio} src={referenceSrc ?? undefined} preload="metadata" muted={muteRef} onLoadedMetadata={refreshLength} data-testid="reference-audio" />
          <label>
            <input type="checkbox" checked={muteRef} onChange={(e) => setMuteRef(e.target.checked)} />
            {t('compare.muteReference')}
          </label>
        </div>
        <div className="track">
          <h3>{attemptLabel ?? t('compare.attempt')}</h3>
          <audio ref={attemptAudio} src={attemptSrc ?? undefined} preload="metadata" muted={muteAttempt} onLoadedMetadata={refreshLength} data-testid="attempt-audio" />
          <label>
            <input type="checkbox" checked={muteAttempt} onChange={(e) => setMuteAttempt(e.target.checked)} />
            {t('compare.muteAttempt')}
          </label>
        </div>
      </div>
      {!ready && <p className="muted">{t('compare.noAudio')}</p>}
      <div className="controls" role="group" aria-label={t('compare.hint')}>
        <button
          type="button"
          disabled={!ready}
          aria-pressed={playing}
          onClick={() => {
            const c = controller.current;
            if (!c) return;
            if (playing) {
              c.pause();
              setPlaying(false);
            } else {
              c.play();
              setLength(c.length);
              setPlaying(true);
            }
          }}
        >
          {playing ? t('compare.pause') : t('compare.play')}
        </button>
        <button
          type="button"
          disabled={!ready}
          onClick={() => {
            controller.current?.restart();
            setPosition(0);
          }}
        >
          {t('compare.restart')}
        </button>
        <label htmlFor={seekId}>{t('compare.seek')}</label>
        <input
          id={seekId}
          type="range"
          min={0}
          max={Math.max(length, 0.01)}
          step={0.05}
          value={Math.min(position, Math.max(length, 0.01))}
          disabled={!ready}
          aria-valuetext={`${formatDuration(Math.round(position))} / ${formatDuration(Math.round(length))}`}
          onChange={(e) => {
            const p = Number(e.target.value);
            controller.current?.seek(p);
            setPosition(p);
          }}
        />
        <span className="muted" aria-hidden="true">
          {formatDuration(Math.round(position))} / {formatDuration(Math.round(length))}
        </span>
        <label htmlFor={offsetId}>{t('compare.offset')}</label>
        <input
          id={offsetId}
          type="number"
          step={0.1}
          value={offset}
          onChange={(e) => {
            const value = Number(e.target.value) || 0;
            setOffset(value);
            controller.current?.setOffset(value);
            refreshLength();
          }}
        />
      </div>
    </section>
  );
}
